import { useState, useEffect, useRef, useMemo } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, RefreshCw, Brain, Lock, Settings, ChevronLeft, Search, X } from "lucide-react";
import { useToast } from "../components/Toast";
import UnlockModal from "../components/UnlockModal";
import { useAuth } from "../contexts/AuthContext";
import { hasMatchAnalyticsAccess, hasPaidAnalyticsAccess, isAvailableLockedPick } from "../lib/lockedPickLink";

// ── Helpers ──────────────────────────────────────────────────────────────────

const calcEdge = (aiProb, odds) => {
  if (!odds || odds <= 0) return null;
  const impl = 1 / odds;
  const edge = aiProb - impl;
  return { edge, edgePct: Math.abs(edge * 100).toFixed(0), hasValue: edge > 0.02 };
};

const poissonPMF = (k, lam) => {
  let f = 1; for (let i = 2; i <= k; i++) f *= i;
  return Math.exp(-lam) * Math.pow(lam, k) / f;
};
const poissonCDF = (maxK, lam) => {
  let s = 0; for (let k = 0; k <= maxK; k++) s += poissonPMF(k, lam); return s;
};

const abbr = (name) => (name || '???').replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase();

const dayStart = (offset = 0) => {
  const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + offset); return d.getTime();
};

// ── Blur wrapper ──────────────────────────────────────────────────────────────

function BlurredSection({ children, isBlurred, router, onUnlock }) {
  if (!isBlurred) return children;
  return (
    <div className="analytics-premium-state relative" data-testid="analytics-locked-section">
      <div className="min-h-32 flex items-center justify-center bg-black/20 rounded-2xl">
        <button
          onClick={onUnlock || (() => router.push('/profile'))}
          className="flex items-center gap-2 px-4 py-2 bg-[#FFD700] hover:bg-[#FFD700]/90 text-black text-sm font-bold rounded-lg shadow-lg transition-all hover:scale-105"
        >
          <Lock className="w-4 h-4" />
          Unlock
        </button>
      </div>
    </div>
  );
}

// ── Constants ─────────────────────────────────────────────────────────────────

const SPORTS = [
  { id: 'football', name: 'Football' },
  { id: 'rugby',    name: 'Rugby' },
  { id: 'mma',      name: 'MMA' },
  { id: 'basketball', name: 'Basketball' },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function Analytics() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { user: userInfo } = useAuth();
  const { addToast } = useToast();

  const [selectedSport, setSelectedSport]       = useState('football');
  const [competitions, setCompetitions]         = useState([]);
  const [selectedComp, setSelectedComp]         = useState(null);
  const [fixtures, setFixtures]                 = useState([]);
  const [selectedFixture, setSelectedFixture]   = useState(null);
  const [predictionData, setPredictionData]     = useState(null);
  const [loading, setLoading]                   = useState(false);
  const [error, setError]                       = useState(null);
  const [compDropdownOpen, setCompDropdownOpen] = useState(false);
  const compDropdownRef                         = useRef(null);
  const [oddsData, setOddsData]                 = useState(null);
  const [isBasicLimitReached, setIsBasicLimitReached] = useState(false);
  const [analyticsQuota, setAnalyticsQuota]     = useState(null);
  const [unlockModal, setUnlockModal]           = useState({ isOpen: false, matchId: null, coinCost: 1 });
  const [pendingFixtureId, setPendingFixtureId] = useState(null);
  const [pendingLeagueId, setPendingLeagueId]   = useState(null);
  const matchParamHandled                       = useRef(false);

  // ── Mobile state ──────────────────────────────────────────────────────────
  const [selectedDate, setSelectedDate]           = useState(() => dayStart(0));
  const [mobileView, setMobileView]               = useState('list');
  const [mobileAllFixtures, setMobileAllFixtures] = useState([]);
  const [mobileFetching, setMobileFetching]       = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const dateStripRef                              = useRef(null);

  const saveComp = (c) => { if (typeof window !== 'undefined' && c) localStorage.setItem('selectedCompId', String(c.id)); };

  const { data: userUnlocks, isSuccess: unlocksLoaded, isFetching: unlocksFetching } = useQuery({
    queryKey: ['skillcoins', 'unlocks', userInfo?.user_id],
    queryFn: async () => {
      const r = await fetch('/api/skillcoins/unlocks', { credentials: 'include' });
      if (!r.ok) throw new Error('Unable to check match unlocks');
      const data = await r.json();
      if (data?.success !== true || !Array.isArray(data.match_ids)) throw new Error('Invalid match unlocks');
      return data;
    },
    enabled: !!userInfo?.user_id,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });

  const handleUnlockClick = (matchId, coinCost = 1) => setUnlockModal({ isOpen: true, matchId, coinCost });
  const handleUnlocked = () => {
    queryClient.invalidateQueries({ queryKey: ['skillcoins', 'unlocks', userInfo?.user_id] });
  };

  // URL flags and browser state never grant access. A failed or pending lookup
  // cannot reuse unlocks from a previous account or an earlier successful fetch.
  const isFreeTier = !hasPaidAnalyticsAccess(userInfo);
  const isCurrentMatchLocked = !hasMatchAnalyticsAccess(
    userInfo,
    unlocksLoaded && !unlocksFetching ? userUnlocks : null,
    selectedFixture?.fixture?.id
  );

  // Record each locked fixture viewed; never treat a different fixture as the viewed one.
  const lockedPickFiredRef = useRef(new Set());
  useEffect(() => {
    const fixtureId = Number(selectedFixture?.fixture?.id);
    if (!isCurrentMatchLocked || !userInfo || unlocksFetching || !unlocksLoaded || !Number.isSafeInteger(fixtureId) || fixtureId <= 0) return;
    if (lockedPickFiredRef.current.has(fixtureId)) return;
    lockedPickFiredRef.current.add(fixtureId);
    fetch('/api/events/locked-pick-viewed', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fixture_id: fixtureId }),
    }).catch(() => {});
  }, [isCurrentMatchLocked, selectedFixture, userInfo, unlocksFetching, unlocksLoaded]);

  const refreshQuota = async () => {
    try {
      const r = await fetch('/api/analytics/quota', { credentials: 'include' });
      if (r.ok) { const q = await r.json(); setAnalyticsQuota(q); if (q.tier === 'basic' && q.remaining === 0) setIsBasicLimitReached(true); }
    } catch {}
  };

  useEffect(() => {
    if (!router.isReady) return;
    const mp = router.query.match;
    if (!mp) return;
    const lockedPickLink = router.query.locked_pick === '1';
    const fid = typeof mp === 'string' && /^[1-9]\d*$/.test(mp) ? Number(mp) : NaN;
    if (!Number.isSafeInteger(fid)) {
      setSelectedFixture(null);
      setPredictionData(null);
      setMobileView('detail');
      setError('This match link is invalid.');
      return;
    }

    // Directly fetch prediction + fixture data — skips the competition→fixtures waterfall
    // which fails when the AI pick comes from a league not in the Pro Tips whitelist.
    setPredictionData(null);
    setSelectedFixture(null);
    setError(null);
    setLoading(true);
    setMobileView('detail');
    fetch(`/api/football/predictions?fixture=${fid}`, { credentials: 'include' })
      .then(r => {
        if (!r.ok) throw new Error('Match unavailable');
        return r.json();
      })
      .then(d => {
        const raw = d.raw;
        if (lockedPickLink) {
          if (!isAvailableLockedPick(d, fid)) {
            setError('This pick is no longer available. No other match has been selected.');
            return;
          }
        }
        if (d.prediction) setPredictionData(d.prediction);
        if (raw) {
          const fx = {
            fixture: raw.fixture || { id: fid },
            teams: raw.teams || {},
            league: raw.league || {},
            _directLoad: true,
          };
          setSelectedFixture(fx);
          setMobileView('detail');
          // NOTE: do NOT call setPendingLeagueId here — it triggers the
          // competition→fixtures waterfall which overwrites selectedFixture.
        }
      })
      .catch(() => setError('This match could not be loaded. No other match has been selected.'))
      .finally(() => setLoading(false));
  }, [router.isReady, router.query.match, router.query.locked_pick]);

  useEffect(() => {
    if (pendingLeagueId && competitions.length > 0) {
      const c = competitions.flatMap(g => g.items).find(c => c.id === pendingLeagueId);
      if (c) { setSelectedComp(c); saveComp(c); setPendingLeagueId(null); }
    }
  }, [pendingLeagueId, competitions]);

  useEffect(() => {
    if (userInfo?.subscription_tier?.toLowerCase() === 'basic' && hasPaidAnalyticsAccess(userInfo)) refreshQuota();
  }, [userInfo?.user_id, userInfo?.subscription_tier, userInfo?.subscription_status]);

  useEffect(() => {
    const h = (e) => { if (compDropdownRef.current && !compDropdownRef.current.contains(e.target)) setCompDropdownOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (!router.isReady) return;
    fetch('/api/football/whitelist').then(r => r.json()).then(data => {
      const groups = data.groups || [];
      setCompetitions(groups);
      if (!router.query.match) {
        const all = groups.flatMap(g => g.items);
        const savedId = typeof window !== 'undefined' ? localStorage.getItem('selectedCompId') : null;
        if (savedId) { const s = all.find(c => c.id === parseInt(savedId, 10)); if (s) { setSelectedComp(s); return; } }
        const pl = all.find(c => c.id === 39);
        if (pl) { setSelectedComp(pl); saveComp(pl); }
        else if (groups.length && groups[0].items.length) { setSelectedComp(groups[0].items[0]); saveComp(groups[0].items[0]); }
      }
    }).catch(() => {});
  }, [router.isReady, router.query.match]);

  useEffect(() => {
    if (!selectedComp || router.query.locked_pick === '1') return;
    setLoading(true); setError(null);
    fetch(`/api/football/fixtures?competitionIds=${selectedComp.id}&season=${selectedComp.season || 2025}`)
      .then(r => r.json()).then(data => {
        const fxs = data.fixtures || [];
        setFixtures(fxs);
        if (pendingFixtureId) {
          const m = fxs.find(f => f.fixture?.id === pendingFixtureId);
          if (m) { setSelectedFixture(m); setMobileView('detail'); setPendingFixtureId(null); return; }
        }
        // Never overwrite a fixture that was loaded directly from the URL param
        if (selectedFixture?._directLoad) return;
        const sid = typeof window !== 'undefined' ? localStorage.getItem('selectedFixtureId') : null;
        if (sid) { const m = fxs.find(f => f.fixture?.id === parseInt(sid, 10)); if (m) { setSelectedFixture(m); return; } }
        if (fxs.length) { setSelectedFixture(fxs[0]); localStorage.setItem('selectedFixtureId', String(fxs[0].fixture?.id)); }
      }).catch(() => setError('Failed to load fixtures')).finally(() => setLoading(false));
  }, [selectedComp, pendingFixtureId]);

  // Mobile path: when all fixtures are loaded, find the pending fixture across all competitions
  useEffect(() => {
    if (!pendingFixtureId || mobileAllFixtures.length === 0) return;
    const m = mobileAllFixtures.find(f => f.fixture?.id === pendingFixtureId);
    if (m) {
      setSelectedFixture(m);
      setMobileView('detail');
      setPendingFixtureId(null);
    }
  }, [pendingFixtureId, mobileAllFixtures]);

  // ── Mobile: fetch ALL competitions' fixtures in parallel ──────────────────
  useEffect(() => {
    if (!router.isReady || competitions.length === 0) return;
    const all = competitions.flatMap(g => g.items);
    if (!all.length) return;
    setMobileFetching(true);
    Promise.all(
      all.map(comp =>
        fetch(`/api/football/fixtures?competitionIds=${comp.id}&season=${comp.season || 2025}`)
          .then(r => r.json())
          .then(data => (data.fixtures || []).map(fx => ({ ...fx, _compId: comp.id, _comp: comp })))
          .catch(() => [])
      )
    ).then(results => setMobileAllFixtures(results.flat()))
      .finally(() => setMobileFetching(false));
  }, [competitions, router.isReady]);

  useEffect(() => {
    if (!selectedFixture) return;
    const fid = selectedFixture.fixture?.id;
    if (!fid) return;
    // Skip re-fetch when this fixture was already loaded directly from the URL param effect
    if (selectedFixture._directLoad) return;
    setLoading(true); setError(null); setOddsData(null);
    fetch(`/api/football/predictions?fixture=${fid}`).then(r => r.json()).then(d => {
      if (d.prediction) setPredictionData(d.prediction);
      else setError('No prediction data available for this fixture');
    }).catch(() => setError('Failed to load prediction data')).finally(() => setLoading(false));

    if (selectedComp?.id && selectedComp?.season) {
      fetch(`/api/football/odds?league=${selectedComp.id}&season=${selectedComp.season || 2025}`)
        .then(r => r.json()).then(d => {
          const fx = (d.odds || []).find(i => i.fixture?.id === fid);
          if (!fx) return;
          for (const bk of (fx.bookmakers || [])) {
            const mw = (bk.bets || []).find(b => b.name === 'Match Winner');
            if (mw?.values) {
              const h = parseFloat(mw.values.find(v => v.value === 'Home')?.odd);
              const dr = parseFloat(mw.values.find(v => v.value === 'Draw')?.odd);
              const a = parseFloat(mw.values.find(v => v.value === 'Away')?.odd);
              if (h && dr && a) { setOddsData({ home: h, draw: dr, away: a }); break; }
            }
          }
        }).catch(() => {});
    }
  }, [selectedFixture, selectedComp]);

  const buildWhyText = (pred) => {
    if (!pred) return '';
    const hName = pred.home?.name || 'Home';
    const aName = pred.away?.name || 'Away';
    const hElo  = Math.round((pred.comparison?.total?.home || 0.5) * 100);
    const hForm = Math.round((pred.comparison?.form?.home  || 0.5) * 100);
    const aDef  = Math.round((pred.comparison?.def?.away   || pred.comparison?.defense?.away || 0.5) * 100);
    const top   = (pred.prediction || '').toLowerCase();
    if (top.includes('away') || top.includes((aName || '').toLowerCase())) {
      return `${aName}'s away ELO (${100 - hElo}%) and defensive form (${aDef}%) suppresses ${hName}'s attack effectively.`;
    }
    if (top.includes('draw')) {
      return `${hName}'s recent form (${hForm}%) and ${aName}'s defensive strength (${aDef}%) point to a tightly contested match.`;
    }
    const poi_h = pred.comparison?.poisson_distribution?.home ?? 0.5;
    const xg = (poi_h * 2.65).toFixed(2);
    return `${hName}'s time-decayed attack form (${xg} expected goals) holds a significant tactical advantage over ${aName}'s away ELO baseline.`;
  };

  // ── Mobile date strip days ────────────────────────────────────────────────
  const mobileDates = useMemo(() => Array.from({ length: 7 }, (_, i) => dayStart(i - 1)), []);

  // ── Mobile: fixtures for selected date or active 30-day search ─────────────
  const mobileGrouped = useMemo(() => {
    const groups = {};
    const query = mobileSearchQuery.trim().toLowerCase();
    const isSearchActive = query.length > 0;
    const searchWindowStart = dayStart(0);
    const searchWindowEnd = dayStart(30);

    mobileAllFixtures.forEach(fx => {
      const ts = fx.fixture?.timestamp;
      if (!ts) return;
      const d = new Date(ts * 1000); d.setHours(0, 0, 0, 0);
      const fixtureDate = d.getTime();
      if (isSearchActive
        ? (fixtureDate < searchWindowStart || fixtureDate >= searchWindowEnd)
        : fixtureDate !== selectedDate) return;
      if (query) {
        const searchableText = [
          fx.teams?.home?.name,
          fx.teams?.away?.name,
          fx.league?.name,
          fx._comp?.name,
          fx._comp?.country,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!searchableText.includes(query)) return;
      }
      const key = fx._compId;
      if (!groups[key]) groups[key] = { comp: fx._comp, fixtures: [] };
      groups[key].fixtures.push(fx);
    });
    return Object.values(groups).sort((a, b) => a.comp.name.localeCompare(b.comp.name));
  }, [mobileAllFixtures, selectedDate, mobileSearchQuery]);

  // ── Mobile: tap a match → load predictions ────────────────────────────────
  const handleMobileMatchTap = (fx, comp) => {
    setSelectedFixture(fx);
    setSelectedComp(comp);
    localStorage.setItem('selectedFixtureId', String(fx.fixture?.id));
    setPredictionData(null);
    setError(null);
    setMobileView('detail');
    window.scrollTo(0, 0);
  };

  // ── Shared stat calculations ──────────────────────────────────────────────
  const calcStats = (pred) => {
    if (!pred) return null;
    const hw = pred.win_probabilities?.home ?? 0.34;
    const dw = pred.win_probabilities?.draw ?? 0.33;
    const aw = pred.win_probabilities?.away ?? 0.33;
    const hwPct = Math.round(hw * 100);
    const dwPct = Math.round(dw * 100);
    const awPct = Math.round(aw * 100);
    const highestPct = Math.max(hwPct, dwPct, awPct);
    const leadingOutcome = hwPct === highestPct ? 'home' : dwPct === highestPct ? 'draw' : 'away';
    const conf    = pred.confidence ?? Math.round(Math.max(hw, dw, aw) * 100);
    const confLbl = pred.confidence_level ?? (conf >= 75 ? 'High' : conf >= 55 ? 'Medium' : 'Low');
    const homeEdge = oddsData ? calcEdge(hw, oddsData.home) : null;
    const drawEdge = oddsData ? calcEdge(dw, oddsData.draw) : null;
    const awayEdge = oddsData ? calcEdge(aw, oddsData.away) : null;
    const impl_h = oddsData ? Math.round((1 / oddsData.home) * 100) : null;
    const impl_d = oddsData ? Math.round((1 / oddsData.draw) * 100) : null;
    const impl_a = oddsData ? Math.round((1 / oddsData.away) * 100) : null;
    const confBadgeCls = conf >= 75
      ? 'bg-green-500/20 border-green-500/40 text-green-300'
      : conf >= 55
        ? 'bg-[#d97706]/20 border-[#d97706]/40 text-[#fbbf24]'
        : 'bg-red-500/20 border-red-500/40 text-red-300';
    const poi_h = pred.comparison?.poisson_distribution?.home ?? 0.5;
    const poi_a = pred.comparison?.poisson_distribution?.away ?? 0.5;
    const AVG   = 2.65;
    const xg_h  = +(poi_h * AVG).toFixed(2);
    const xg_a  = +(poi_a * AVG).toFixed(2);
    const tot   = xg_h + xg_a;
    const over15Pct = Math.min(99, Math.round((1 - poissonCDF(1, tot)) * 100));
    const over25Pct = Math.min(99, Math.round((1 - poissonCDF(2, tot)) * 100));
    const bttsPct   = Math.round((1 - Math.exp(-xg_h)) * (1 - Math.exp(-xg_a)) * 100);
    const cs_h = Math.round(Math.exp(-xg_a) * 100);
    const cs_a = Math.round(Math.exp(-xg_h) * 100);
    const eloShare_h = pred.comparison?.total?.home ?? 0.5;
    const eloShare_a = pred.comparison?.total?.away ?? 0.5;
    const elo_h = Math.round(1500 + (eloShare_h - 0.5) * 800);
    const elo_a = Math.round(1500 + (eloShare_a - 0.5) * 800);
    const eloBarH = Math.round(eloShare_h * 100);
    const attShare_h = pred.comparison?.att?.home ?? 0.5;
    const attShare_a = pred.comparison?.att?.away ?? 0.5;
    const attPctH = Math.round(attShare_h * 100);
    const att_h = (attShare_h * 3).toFixed(2);
    const att_a = (attShare_a * 3).toFixed(2);
    const attBarH = Math.round(attShare_h * 100);
    const defShare_h = pred.comparison?.def?.home ?? 0.5;
    const defShare_a = pred.comparison?.def?.away ?? 0.5;
    const defPctH = Math.round(defShare_h * 100);
    const defCon_h = ((1 - defShare_h) * 2.5).toFixed(2);
    const defCon_a = ((1 - defShare_a) * 2.5).toFixed(2);
    const defBarH = Math.round(defShare_h * 100);
    const form_h = Math.round((pred.comparison?.form?.home ?? 0.5) * 100);
    const form_a = Math.round((pred.comparison?.form?.away ?? 0.5) * 100);
    const recSt_h = form_h >= 65 ? 'Optimal' : form_h >= 45 ? 'Average' : 'Fatigue Warning';
    const recSt_a = form_a >= 65 ? 'Optimal' : form_a >= 45 ? 'Average' : 'Fatigue Warning';
    const recClr_h = form_h >= 65 ? 'text-green-400' : form_h >= 45 ? 'text-yellow-400' : 'text-red-400';
    const recClr_a = form_a >= 65 ? 'text-green-400' : form_a >= 45 ? 'text-yellow-400' : 'text-red-400';
    const hName = pred.home?.name || 'Home';
    const aName = pred.away?.name || 'Away';
    const hAbbr = abbr(hName);
    const aAbbr = abbr(aName);
    return {
      hw, dw, aw, hwPct, dwPct, awPct, leadingOutcome, conf, confLbl, confBadgeCls,
      homeEdge, drawEdge, awayEdge, impl_h, impl_d, impl_a,
      xg_h, xg_a, over15Pct, over25Pct, bttsPct, cs_h, cs_a,
      elo_h, elo_a, eloBarH, attPctH, att_h, att_a, attBarH,
      defPctH, defCon_h, defCon_a, defBarH,
      form_h, form_a, recSt_h, recSt_a, recClr_h, recClr_a,
      hName, aName, hAbbr, aAbbr,
    };
  };

  return (
    <>
      <Head>
        <title>Pro Tips - SkillBets</title>
        <meta name="description" content="ML-powered match predictions and analytics" />
      </Head>

      <UnlockModal
        isOpen={unlockModal.isOpen}
        onClose={() => setUnlockModal({ isOpen: false, matchId: null, coinCost: 1 })}
        matchId={unlockModal.matchId}
        coinCost={unlockModal.coinCost}
        onUnlocked={handleUnlocked}
      />

      <div className="analytics-workstation min-h-screen bg-[#0A0E1B] text-white">

        {/* Page header */}
        <div className="analytics-page-header border-b border-[#2C3555]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-5">
            <div className="analytics-header-content flex items-center gap-3.5">
              <div className="analytics-header-mark" aria-hidden="true">
                <BarChart3 className="w-5 h-5 text-[#6B9CFF]" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold">Pro Tips</h1>
                <p className="text-xs mt-0.5 hidden sm:block">ML-powered match predictions · XGBoost + ELO + Poisson</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sport tabs */}
        <div className="analytics-sport-tabs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex gap-2">
              {SPORTS.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSelectedSport(s.id)}
                  aria-pressed={selectedSport === s.id}
                  className={`flex items-center justify-center px-2 py-2 rounded-xl font-bold transition-all flex-1 ${
                    selectedSport === s.id ? 'bg-[#3EA8F5] text-white shadow-lg shadow-[#3EA8F5]/30' : 'bg-white/5 text-[#9ca3af] hover:bg-white/10 hover:text-white'
                  }`}>
                  <span className="text-sm leading-none">{s.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════════
            MOBILE  (hidden on lg+)
        ════════════════════════════════════════════════ */}
        <div className="analytics-mobile-shell block lg:hidden">

          {selectedSport !== 'football' && (
            <div className="flex flex-col items-center justify-center py-20 text-center px-6">
              <h2 className="text-2xl font-bold mb-3">{SPORTS.find(s => s.id === selectedSport)?.name} Analytics</h2>
              <div className="bg-[#3EA8F5]/10 border border-[#3EA8F5]/30 rounded-2xl p-8">
                <p className="text-xl font-semibold text-[#3EA8F5] mb-2">Coming Soon</p>
                <p className="text-[#e5e7eb] text-sm">We're working to bring you {SPORTS.find(s => s.id === selectedSport)?.name} predictions soon.</p>
              </div>
            </div>
          )}

          {selectedSport === 'football' && (
            <>
              {/* ── LIST: date strip + league cards ── */}
              {mobileView === 'list' && (
                <>
                  {/* Sticky date strip */}
                  <div ref={dateStripRef}
                    className="flex gap-1.5 overflow-x-auto scrollbar-hide px-3 pt-4 pb-3 bg-[#0f1926] border-b border-[#1e2d3d] sticky top-0 z-20">
                    {mobileDates.map(ts => {
                      const d = new Date(ts);
                      const isToday = ts === dayStart(0);
                      const isSelected = ts === selectedDate;
                      const dayLbl = isToday ? 'TODAY' : d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
                      const dateLbl = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      return (
                        <button key={ts}
                          onClick={() => setSelectedDate(ts)}
                          className={`flex flex-col items-center shrink-0 min-w-[62px] px-2 py-2 rounded-xl transition-all ${
                            isSelected ? 'bg-white' : 'hover:bg-white/8'
                          }`}>
                          <span className={`text-[9px] font-bold leading-none mb-1 ${isSelected ? 'text-black/60' : 'text-[#9ca3af]'}`}>
                            {dayLbl}
                          </span>
                          <span className={`text-sm font-bold leading-none ${isSelected ? 'text-black' : 'text-white'}`}>
                            {dateLbl}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Mobile fixture search */}
                  <div className="px-3 pt-3">
                    <label htmlFor="mobile-pro-tips-search" className="sr-only">
                      Search teams, leagues or cups
                    </label>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9ca3af]" aria-hidden="true" />
                      <input
                        id="mobile-pro-tips-search"
                        type="search"
                        value={mobileSearchQuery}
                        onChange={(e) => setMobileSearchQuery(e.target.value)}
                        placeholder="Search teams, leagues or cups"
                        autoComplete="off"
                        className="w-full rounded-xl border border-[#1e2d3d] bg-[#132235] py-3 pl-10 pr-10 text-sm text-white placeholder:text-[#718096] outline-none transition-colors focus:border-[#3EA8F5] focus:ring-1 focus:ring-[#3EA8F5]"
                        data-testid="input-mobile-pro-tips-search"
                      />
                      {mobileSearchQuery && (
                        <button
                          type="button"
                          onClick={() => setMobileSearchQuery('')}
                          aria-label="Clear Pro Tips search"
                          className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-[#9ca3af] transition-colors hover:bg-white/10 hover:text-white"
                          data-testid="button-clear-mobile-pro-tips-search"
                        >
                          <X className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Match list */}
                  <div className="analytics-mobile-list pb-24 px-3 pt-3 space-y-3">
                    {mobileFetching && (
                      <div className="flex items-center justify-center py-14">
                        <RefreshCw className="w-6 h-6 animate-spin text-[#3EA8F5]" />
                      </div>
                    )}

                    {!mobileFetching && mobileGrouped.length === 0 && (
                      <div className="text-center py-14">
                        <div className="analytics-empty-mark" aria-hidden="true">{mobileSearchQuery.trim() ? 'SEARCH' : 'DATE'}</div>
                        <p className="text-[#9ca3af] font-medium">
                          {mobileSearchQuery.trim() ? 'No matching fixtures found' : 'No fixtures found for this date'}
                        </p>
                        <p className="text-[#4b5563] text-sm mt-1">
                          {mobileSearchQuery.trim() ? 'Try a different team, league or cup' : 'Try selecting a different day above'}
                        </p>
                      </div>
                    )}

                    {!mobileFetching && mobileGrouped.map(({ comp, fixtures: compFxs }) => (
                      <div key={comp.id} className="rounded-2xl overflow-hidden border border-[#1e2d3d]">

                        {/* League header */}
                        <div className="bg-gradient-to-r from-[#132235] to-[#162d47] px-4 py-3 flex items-center gap-3">
                          {comp.flag ? (
                            <img src={comp.flag} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-white/10 shrink-0" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-[#20334a] flex items-center justify-center text-[10px] font-bold tracking-widest shrink-0">FB</div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-white leading-tight truncate">{comp.name}</div>
                            <div className="text-[10px] text-[#9ca3af] uppercase tracking-wider mt-0.5">
                              {comp.country || 'International'}
                            </div>
                          </div>
                          <BarChart3 className="w-4 h-4 text-[#3EA8F5]/50 shrink-0" />
                        </div>

                        {/* Match rows */}
                        <div className="bg-[#0d1b2a] divide-y divide-[#1a2a3a]">
                          {compFxs
                            .sort((a, b) => (a.fixture?.timestamp || 0) - (b.fixture?.timestamp || 0))
                            .map(fx => {
                              const ts = fx.fixture?.timestamp;
                               const fixtureDate = ts
                                 ? new Date(ts * 1000).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })
                                 : '';
                              const timeStr = ts
                                ? new Date(ts * 1000).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })
                                : '—';
                              const status = fx.fixture?.status?.short;
                              const isLive = ['1H', '2H', 'HT', 'ET', 'BT', 'P', 'INT', 'LIVE'].includes(status);
                              const isFinished = status === 'FT' || status === 'AET' || status === 'PEN';
                              const elapsed = fx.fixture?.status?.elapsed;

                              return (
                                <button key={fx.fixture?.id}
                                  onClick={() => handleMobileMatchTap(fx, comp)}
                                  className="analytics-mobile-fixture w-full flex items-center px-4 py-3.5 gap-2 active:bg-white/5 transition-colors text-left"
                                  data-testid={`mobile-fixture-${fx.fixture?.id}`}>

                                  {/* Home team */}
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <img
                                      src={fx.teams?.home?.logo || '/static/fallback_logo.png'}
                                      alt=""
                                      className="w-6 h-6 object-contain shrink-0"
                                      onError={e => { e.target.onerror = null; e.target.src = '/static/fallback_logo.png'; }}
                                    />
                                    <span className="text-sm font-semibold text-white truncate">{fx.teams?.home?.name}</span>
                                  </div>

                                  {/* Centre: time or score */}
                                  <div className="flex flex-col items-center shrink-0 min-w-[56px] text-center">
                                     {mobileSearchQuery.trim() && (
                                       <span className="text-[9px] font-semibold text-[#9ca3af] leading-none mb-1 whitespace-nowrap">
                                         {fixtureDate}
                                       </span>
                                     )}
                                    {isLive ? (
                                      <>
                                        <span className="text-[9px] font-bold text-red-400 uppercase">
                                          {elapsed ? `${elapsed}'` : 'LIVE'}
                                        </span>
                                        <span className="text-sm font-black text-white leading-tight">
                                          {fx.goals?.home ?? 0} – {fx.goals?.away ?? 0}
                                        </span>
                                      </>
                                    ) : isFinished ? (
                                      <>
                                        <span className="text-[9px] font-bold text-[#9ca3af] uppercase">FT</span>
                                        <span className="text-sm font-black text-white leading-tight">
                                          {fx.goals?.home ?? 0} – {fx.goals?.away ?? 0}
                                        </span>
                                      </>
                                    ) : (
                                      <span className="text-sm font-bold text-[#3EA8F5]">{timeStr}</span>
                                    )}
                                  </div>

                                  {/* Away team */}
                                  <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                                    <span className="text-sm font-semibold text-white truncate text-right">{fx.teams?.away?.name}</span>
                                    <img
                                      src={fx.teams?.away?.logo || '/static/fallback_logo.png'}
                                      alt=""
                                      className="w-6 h-6 object-contain shrink-0"
                                      onError={e => { e.target.onerror = null; e.target.src = '/static/fallback_logo.png'; }}
                                    />
                                  </div>

                                  <span className="text-[#3EA8F5] text-base shrink-0 ml-1">›</span>
                                </button>
                              );
                            })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* ── DETAIL: prediction panel ── */}
              {mobileView === 'detail' && (() => {
                const s = predictionData ? calcStats(predictionData) : null;
                return (
                  <div className="analytics-mobile-detail pb-24" data-testid="analytics-mobile-detail">
                    {/* Sticky back bar */}
                    <div className="sticky top-0 z-20 bg-[#0f1926]/95 backdrop-blur-sm border-b border-[#1e2d3d] px-4 py-3 flex items-center gap-3">
                      <button
                        onClick={() => setMobileView('list')}
                        className="flex items-center gap-1 text-[#3EA8F5] font-semibold text-sm shrink-0">
                        <ChevronLeft className="w-4 h-4" />
                        Back
                      </button>
                      {selectedFixture && (
                        <div className="flex-1 text-center min-w-0">
                          <p className="text-xs font-bold text-white truncate">
                            {selectedFixture.teams?.home?.name} vs {selectedFixture.teams?.away?.name}
                          </p>
                          <p className="text-[10px] text-[#9ca3af] truncate">{selectedComp?.name}</p>
                        </div>
                      )}
                    </div>

                    {/* Kickoff time */}
                    {selectedFixture?.fixture?.timestamp && (
                      <div className="text-center pt-3 pb-1 px-4">
                        <p className="text-[10px] text-[#9ca3af] uppercase tracking-wider">
                          {new Date(selectedFixture.fixture.timestamp * 1000).toLocaleString('en-ZA', {
                            weekday: 'short', month: 'short', day: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </p>
                      </div>
                    )}

                    {/* Loading */}
                    {loading && (
                      <div className="flex items-center justify-center py-16">
                        <RefreshCw className="w-7 h-7 animate-spin text-[#3EA8F5]" />
                      </div>
                    )}

                    {/* Error */}
                    {!loading && error && (
                      <div className="mx-4 mt-4 bg-red-500/10 border border-red-500/30 rounded-2xl p-6 text-center">
                        <p className="text-red-300 text-sm">{error}</p>
                      </div>
                    )}

                    {/* Prediction content */}
                    {!loading && !error && predictionData && s && (
                      <div className="px-3 space-y-3 mt-2">

                        {/* ── Teams + probability bar ── */}
                        <BlurredSection isBlurred={isCurrentMatchLocked} router={router}
                          onUnlock={() => handleUnlockClick(selectedFixture?.fixture?.id, 1)}>
                          <div className="bg-[#132235] border border-[#20334a] rounded-2xl p-4">
                            {/* Team row */}
                            <div className="flex items-center gap-2 mb-4">
                              <div className="flex flex-col items-center gap-1.5 w-[72px] shrink-0">
                                <div className="w-12 h-12 rounded-full bg-[#1c324b] border border-[#2a4360] flex items-center justify-center">
                                  <img src={predictionData.home?.logo || '/static/fallback_logo.png'} alt={s.hName}
                                    className="w-8 h-8 object-contain"
                                    onError={e => { e.target.onerror = null; e.target.src = '/static/fallback_logo.png'; }} />
                                </div>
                                <span className="text-[11px] font-bold text-white text-center leading-tight line-clamp-2">{s.hName}</span>
                                <span className="text-[9px] text-[#9ca3af] font-bold uppercase">Home</span>
                              </div>

                              <div className="flex-1 flex flex-col gap-2 min-w-0">
                                {/* Probability labels */}
                                <div className="flex justify-between text-[10px] font-bold text-[#d1d5db] px-0.5">
                                  <span>{s.hAbbr} Win</span>
                                  <span>Draw</span>
                                  <span>{s.aAbbr} Win</span>
                                </div>
                                {/* Bar */}
                                <div className="flex h-9 rounded-lg overflow-hidden text-xs font-bold">
                                  <div className={`${s.leadingOutcome === 'home' ? 'analytics-prob-winner' : 'analytics-prob-low'} flex items-center justify-center border-r border-[#0A0E1B]`}
                                    data-probability-outcome="home"
                                    data-leading={s.leadingOutcome === 'home'}
                                    style={{ width: `${s.hwPct}%` }}>
                                    {s.hwPct >= 20 ? `${s.hwPct}%` : ''}
                                  </div>
                                  <div className={`${s.leadingOutcome === 'draw' ? 'analytics-prob-winner' : 'analytics-prob-mid'} flex items-center justify-center border-r border-[#0A0E1B]`}
                                    data-probability-outcome="draw"
                                    data-leading={s.leadingOutcome === 'draw'}
                                    style={{ width: `${s.dwPct}%` }}>
                                    {s.dwPct >= 15 ? `${s.dwPct}%` : ''}
                                  </div>
                                  <div className={`${s.leadingOutcome === 'away' ? 'analytics-prob-winner' : 'analytics-prob-low'} flex items-center justify-center`}
                                    data-probability-outcome="away"
                                    data-leading={s.leadingOutcome === 'away'}
                                    style={{ width: `${s.awPct}%` }}>
                                    {s.awPct >= 20 ? `${s.awPct}%` : ''}
                                  </div>
                                </div>
                                {/* Implied market */}
                                {s.impl_h !== null && (
                                  <div className="text-[9px] text-[#9ca3af] text-center">
                                    Market: {s.impl_h}% / {s.impl_d}% / {s.impl_a}% (H/D/A)
                                  </div>
                                )}
                              </div>

                              <div className="flex flex-col items-center gap-1.5 w-[72px] shrink-0">
                                <div className="w-12 h-12 rounded-full bg-[#1c324b] border border-[#2a4360] flex items-center justify-center">
                                  <img src={predictionData.away?.logo || '/static/fallback_logo.png'} alt={s.aName}
                                    className="w-8 h-8 object-contain"
                                    onError={e => { e.target.onerror = null; e.target.src = '/static/fallback_logo.png'; }} />
                                </div>
                                <span className="text-[11px] font-bold text-white text-center leading-tight line-clamp-2">{s.aName}</span>
                                <span className="text-[9px] text-[#9ca3af] font-bold uppercase">Away</span>
                              </div>
                            </div>

                            {/* Confidence + edges */}
                                <div className="analytics-recommendation bg-[#0f1926]/70 border border-[#1e2d3d] rounded-xl px-3 py-2.5 flex items-center justify-between gap-2">
                                  <span className="text-[10px] font-bold text-[#d1d5db] uppercase tracking-widest">Top recommendation</span>
                                  <span className={`analytics-confidence-badge text-[10px] font-bold px-2.5 py-1 rounded-lg border ${s.confBadgeCls}`}>
                                    {s.confLbl} confidence ({s.conf}%)
                              </span>
                            </div>

                            {(s.homeEdge?.hasValue || s.drawEdge?.hasValue || s.awayEdge?.hasValue) && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {s.homeEdge?.hasValue && <span className="bg-green-500/20 border border-green-500/30 text-green-400 text-[9px] px-2 py-0.5 rounded font-bold">{s.hName} +{s.homeEdge.edgePct}% Edge</span>}
                                {s.drawEdge?.hasValue && <span className="bg-green-500/20 border border-green-500/30 text-green-400 text-[9px] px-2 py-0.5 rounded font-bold">Draw +{s.drawEdge.edgePct}% Edge</span>}
                                {s.awayEdge?.hasValue && <span className="bg-green-500/20 border border-green-500/30 text-green-400 text-[9px] px-2 py-0.5 rounded font-bold">{s.aName} +{s.awayEdge.edgePct}% Edge</span>}
                              </div>
                            )}

                            <p className="text-[11px] text-[#9ca3af] leading-relaxed mt-2.5">{buildWhyText(predictionData)}</p>
                          </div>
                        </BlurredSection>

                        {/* ── Algorithmic Simulation ── */}
                        <BlurredSection isBlurred={isCurrentMatchLocked} router={router}
                          onUnlock={() => handleUnlockClick(selectedFixture?.fixture?.id, 1)}>
                          <div className="bg-[#132235] border border-[#20334a] rounded-2xl p-4">
                            <div className="text-[10px] font-bold text-[#e5e7eb] uppercase tracking-widest border-b border-[#20334a] pb-3 mb-3">
                              Algorithmic Simulation
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="analytics-stat-cell bg-[#0f1926]/50 border border-[#1e2d3d] rounded-xl p-3">
                                <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider mb-1.5">xG</div>
                                <div className="flex items-center gap-2 text-lg font-black">
                                  <span className="text-green-400">{s.xg_h}</span>
                                  <span className="text-sm text-[#9ca3af] font-normal">|</span>
                                  <span className="text-[#3EA8F5]">{s.xg_a}</span>
                                </div>
                              </div>
                              <div className="analytics-stat-cell bg-[#0f1926]/50 border border-[#1e2d3d] rounded-xl p-3">
                                <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider mb-1.5">Over 1.5</div>
                                <div className="text-lg font-black text-white">{s.over15Pct}%</div>
                              </div>
                              <div className="analytics-stat-cell bg-[#0f1926]/50 border border-[#1e2d3d] rounded-xl p-3">
                                <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider mb-1.5">Over 2.5</div>
                                <div className="text-lg font-black text-white">{s.over25Pct}%</div>
                              </div>
                              <div className="analytics-stat-cell bg-[#0f1926]/50 border border-[#1e2d3d] rounded-xl p-3">
                                <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider mb-1.5">BTTS</div>
                                <div className="text-lg font-black text-white">{s.bttsPct}%</div>
                              </div>
                              <div className="analytics-stat-cell col-span-2 bg-[#0f1926]/50 border border-[#1e2d3d] rounded-xl p-3">
                                <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider mb-2">Clean Sheet Likelihood</div>
                                <div className="flex justify-around">
                                  <div className="text-center">
                                    <div className="text-[9px] text-[#9ca3af] font-bold mb-1">{s.hAbbr}</div>
                                    <div className="text-xl font-black text-green-400">{s.cs_h}%</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-[9px] text-[#9ca3af] font-bold mb-1">{s.aAbbr}</div>
                                    <div className="text-xl font-black text-[#3EA8F5]">{s.cs_a}%</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </BlurredSection>

                        {/* ── Deep Performance ── */}
                        <BlurredSection isBlurred={isCurrentMatchLocked} router={router}
                          onUnlock={() => handleUnlockClick(selectedFixture?.fixture?.id, 1)}>
                          <div className="bg-[#132235] border border-[#20334a] rounded-2xl p-4">
                            <div className="text-[10px] font-bold text-[#e5e7eb] uppercase tracking-widest border-b border-[#20334a] pb-3 mb-3">
                              Deep Performance Comparison
                            </div>
                            <div className="space-y-4">

                              {/* ELO */}
                              <div className="analytics-performance-cell">
                                <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider mb-2">Power Ratings (ELO)</div>
                                <div className="flex items-baseline justify-between mb-1.5">
                                  <span className="text-xl font-black text-green-400">{s.elo_h}</span>
                                  <span className="text-xs font-bold text-[#9ca3af] uppercase">vs</span>
                                  <span className="text-xl font-black text-[#9ca3af]">{s.elo_a}</span>
                                </div>
                                <div className="h-1.5 w-full bg-[#0f1926] rounded-full overflow-hidden flex">
                                  <div className="bg-green-400 rounded-full" style={{ width: `${s.eloBarH}%` }} />
                                  <div className="bg-[#4b5563] rounded-full" style={{ width: `${100 - s.eloBarH}%` }} />
                                </div>
                              </div>

                              {/* Attack */}
                              <div className="analytics-performance-cell">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider">Attack Efficiency</div>
                                  <div className="text-[9px] font-bold text-green-400">{s.attPctH}% vs {100 - s.attPctH}%</div>
                                </div>
                                <div className="space-y-1.5">
                                  {[[s.hAbbr, s.att_h, s.attBarH, 'bg-green-400'], [s.aAbbr, s.att_a, 100 - s.attBarH, 'bg-[#3EA8F5]']].map(([label, val, pct, color]) => (
                                    <div key={label} className="flex items-center gap-2">
                                      <span className="text-[9px] font-bold text-[#e5e7eb] w-6 shrink-0">{label}</span>
                                      <div className="flex-1 h-1.5 bg-[#0f1926] rounded-full overflow-hidden">
                                        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
                                      </div>
                                      <span className="text-[10px] text-[#d1d5db] font-mono w-7 text-right shrink-0">{val}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Defence */}
                              <div className="analytics-performance-cell">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider">Defence <span className="normal-case font-normal">(lower=better)</span></div>
                                  <div className="text-[9px] font-bold text-green-400">{s.defPctH}% vs {100 - s.defPctH}%</div>
                                </div>
                                <div className="space-y-1.5">
                                  {[[s.hAbbr, s.defCon_h, s.defBarH, 'bg-green-400'], [s.aAbbr, s.defCon_a, 100 - s.defBarH, 'bg-[#3EA8F5]']].map(([label, val, pct, color]) => (
                                    <div key={label} className="flex items-center gap-2">
                                      <span className="text-[9px] font-bold text-[#e5e7eb] w-6 shrink-0">{label}</span>
                                      <div className="flex-1 h-1.5 bg-[#0f1926] rounded-full overflow-hidden">
                                        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
                                      </div>
                                      <span className="text-[10px] text-[#d1d5db] font-mono w-7 text-right shrink-0">{val}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Recovery */}
                              <div className="analytics-performance-cell">
                                <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider mb-2">Physical Recovery Index</div>
                                <div className="grid grid-cols-2 gap-2">
                                  {[[s.hAbbr, s.form_h, s.recClr_h, s.recSt_h], [s.aAbbr, s.form_a, s.recClr_a, s.recSt_a]].map(([label, val, clr, status]) => (
                                    <div key={label} className={`bg-[#0f1926]/60 rounded-xl p-2.5 text-center border ${val >= 65 ? 'border-green-500/30' : val >= 45 ? 'border-yellow-500/30' : 'border-red-500/30'}`}>
                                      <div className="text-[9px] font-bold text-[#e5e7eb] mb-1">{label}</div>
                                      <div className={`text-xl font-black ${clr}`}>{val}%</div>
                                      <div className={`text-[8px] font-bold mt-0.5 ${clr}`}>{status}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </BlurredSection>
                      </div>
                    )}

                    {!loading && !error && !predictionData && selectedFixture && (
                      <div className="mx-4 mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6 text-center">
                        <p className="text-yellow-200 text-sm">No prediction data available for this fixture</p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>

        {/* ════════════════════════════════════════════════
            DESKTOP  (hidden on mobile)
        ════════════════════════════════════════════════ */}
        <div className="analytics-desktop-shell hidden lg:block">
          <div className="analytics-desktop-workspace max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative">

            {/* Basic tier limit overlay */}
            {!isFreeTier && isBasicLimitReached && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0b131e]/80 backdrop-blur-sm pt-20">
                <div className="max-w-xl mx-auto px-6">
                  <div className="bg-[#132235] border-2 border-[#FFD700] rounded-3xl p-10 text-center shadow-2xl">
                    <BarChart3 className="w-16 h-16 text-[#FFD700] mx-auto mb-4" />
                    <h2 className="text-3xl font-bold mb-3 bg-gradient-to-r from-[#FFD700] to-[#FFA500] bg-clip-text text-transparent">Monthly Limit Reached</h2>
                    <p className="text-[#d1d5db] mb-6">You've used all {analyticsQuota?.limit || 15} Pro Tips analyses this month.</p>
                    <button onClick={() => router.push('/subscribe')} className="w-full px-6 py-3 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black font-bold rounded-xl mb-3 transition-all hover:scale-105">Upgrade — Unlimited Access</button>
                    <button onClick={() => router.push('/predictions/make')} className="w-full px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl border border-white/20 transition-all">Back to Simulations</button>
                  </div>
                </div>
              </div>
            )}

            {/* Non-football placeholder */}
            {selectedSport !== 'football' && (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <h2 className="text-2xl font-bold mb-3">{SPORTS.find(s => s.id === selectedSport)?.name} Analytics</h2>
                <div className="bg-[#3EA8F5]/10 border border-[#3EA8F5]/30 rounded-2xl p-8">
                  <p className="text-xl font-semibold text-[#3EA8F5] mb-2">Coming Soon</p>
                  <p className="text-[#e5e7eb] text-sm">We're working to bring you {SPORTS.find(s => s.id === selectedSport)?.name} predictions soon.</p>
                </div>
              </div>
            )}

            {/* Desktop football layout */}
            {selectedSport === 'football' && (
              <div className="analytics-desktop-grid flex gap-6 pb-10">

                {/* LEFT SIDEBAR */}
                <div className="analytics-desktop-rail w-56 shrink-0 space-y-4">
                  <div ref={compDropdownRef} className="bg-[#0f1926] border border-[#1e2d3d] rounded-2xl p-4 relative">
                    <h3 className="text-[10px] font-bold mb-3 text-[#e5e7eb] uppercase tracking-widest">Competition</h3>
                    <button onClick={() => setCompDropdownOpen(!compDropdownOpen)}
                      className="w-full bg-white/5 border border-[#1e2d3d] rounded-lg px-3 py-2 text-sm text-white text-left focus:outline-none focus:ring-2 focus:ring-[#3EA8F5] flex items-center justify-between"
                      data-testid="select-competition">
                      <span className="flex items-center gap-2">
                        {selectedComp ? (
                          <>{selectedComp.flag && <img src={selectedComp.flag} alt="" className="h-3.5 w-5 object-cover rounded-sm" />}<span>{selectedComp.name}</span></>
                        ) : 'Select competition'}
                      </span>
                      <span className="text-[#e5e7eb] text-xs">{compDropdownOpen ? '▲' : '▼'}</span>
                    </button>
                    {compDropdownOpen && (
                      <div className="absolute z-50 mt-1 left-0 right-0 bg-[#0f1926] border border-[#1e2d3d] rounded-lg shadow-xl max-h-96 overflow-y-auto">
                        {competitions.map(group => (
                          <div key={group.group}>
                            <div className="px-3 py-2 text-xs font-bold text-[#e5e7eb] bg-[#132235] sticky top-0 uppercase tracking-wide">{group.group}</div>
                            {group.items.map(comp => (
                              <button key={comp.id} onClick={() => { setSelectedComp(comp); saveComp(comp); setCompDropdownOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-[#3EA8F5]/20 transition-colors flex items-center gap-2 ${selectedComp?.id === comp.id ? 'bg-[#3EA8F5]/20 text-[#3EA8F5]' : 'text-white'}`}
                                data-testid={`option-competition-${comp.id}`}>
                                {comp.flag ? <img src={comp.flag} alt="" className="h-3.5 w-5 object-cover rounded-sm" /> : <span className="h-3.5 w-5" />}
                                {comp.name}
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="bg-[#0f1926] border border-[#1e2d3d] rounded-2xl p-4">
                    <h3 className="text-[10px] font-bold mb-3 text-[#e5e7eb] uppercase tracking-widest">Upcoming Fixtures</h3>
                    <div className="space-y-2 max-h-[580px] overflow-y-auto pr-1">
                      {fixtures.length === 0 && !loading && <p className="text-sm text-[#e5e7eb]">No upcoming fixtures</p>}
                      {fixtures.map(fx => {
                        const isSel = selectedFixture?.fixture?.id === fx.fixture?.id;
                        return (
                          <button key={fx.fixture?.id}
                            onClick={() => { setSelectedFixture(fx); localStorage.setItem('selectedFixtureId', String(fx.fixture?.id)); }}
                            className={`analytics-desktop-fixture ${isSel ? 'analytics-desktop-fixture-selected' : ''} w-full text-left px-3 py-2.5 rounded-lg border transition-all ${isSel ? 'bg-[#3EA8F5]/20 border-[#3EA8F5]' : 'bg-white/5 border-[#1e2d3d] hover:border-[#3EA8F5]/40'}`}
                            data-testid={`button-fixture-${fx.fixture?.id}`}>
                            <div className="text-[10px] text-[#e5e7eb] mb-1">
                              {new Date(fx.fixture?.timestamp * 1000).toLocaleDateString('en-ZA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </div>
                            <div className="text-sm font-medium text-white">{fx.teams?.home?.name || 'Home'}</div>
                            <div className="text-[10px] text-[#e5e7eb]">vs</div>
                            <div className="text-sm font-medium text-white">{fx.teams?.away?.name || 'Away'}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* MAIN CONTENT */}
                <main className="analytics-desktop-detail flex-1 min-w-0 space-y-4" data-testid="analytics-desktop-detail">
                  {loading && (
                    <div className="flex items-center justify-center py-20">
                      <RefreshCw className="w-7 h-7 animate-spin text-[#3EA8F5]" />
                    </div>
                  )}
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 text-center">
                      <p className="text-red-300 text-sm">{error}</p>
                    </div>
                  )}

                  {!loading && !error && predictionData && (() => {
                    const s = calcStats(predictionData);
                    const kickoff = selectedFixture?.fixture?.timestamp ? new Date(selectedFixture.fixture.timestamp * 1000) : null;
                    const kickoffStr = kickoff
                      ? `${kickoff.toISOString().split('T')[0]} · ${kickoff.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}`
                      : '';
                    return (
                      <>
                        <div className="text-center py-2">
                          <h2 className="analytics-match-heading text-2xl md:text-3xl font-black text-white">
                            {selectedComp?.name} — {s.hName} vs {s.aName}
                          </h2>
                          {kickoffStr && <p className="text-xs text-[#e5e7eb] mt-1 uppercase tracking-wider">{kickoffStr}</p>}
                        </div>

                        {/* 3-Column */}
                        <BlurredSection isBlurred={isCurrentMatchLocked} router={router} onUnlock={() => handleUnlockClick(selectedFixture?.fixture?.id, 1)}>
                          <div className="analytics-match-row grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)] gap-4">
                            <div className="bg-[#132235] border border-[#20334a] rounded-xl p-5 flex flex-col items-center justify-center gap-4 text-center">
                              <div className="w-20 h-20 rounded-full bg-gradient-to-b from-[#1c324b] to-[#132235] border border-[#2a4360] flex items-center justify-center shadow-inner">
                                <img src={predictionData.home?.logo || '/static/fallback_logo.png'} alt={s.hName}
                                  className="w-14 h-14 object-contain"
                                  onError={e => { e.target.onerror = null; e.target.src = '/static/fallback_logo.png'; }} />
                              </div>
                              <div>
                                <div className="text-lg font-extrabold text-white">{s.hName}</div>
                                <div className="text-[10px] font-bold text-[#e5e7eb] uppercase tracking-widest mt-1">Home</div>
                              </div>
                            </div>

                            <div className="bg-[#132235] border border-[#20334a] rounded-xl p-5 flex flex-col gap-4">
                              <div className="flex items-center justify-between border-b border-[#20334a] pb-3">
                                <span className="text-[10px] font-bold text-[#e5e7eb] uppercase tracking-widest">Predictive Matrix</span>
                                <Settings className="w-3.5 h-3.5 text-[#9ca3af]" />
                              </div>
                              <div>
                                <div className="flex justify-between text-xs font-bold text-[#d1d5db] mb-2 px-0.5">
                                  <span>{s.hAbbr} Win</span><span>Draw</span><span>{s.aAbbr} Win</span>
                                </div>
                                <div className="flex h-10 rounded-lg overflow-hidden text-sm font-bold shadow-inner mb-2">
                                  <div className={`${s.leadingOutcome === 'home' ? 'analytics-prob-winner' : 'analytics-prob-low'} flex items-center justify-center border-r border-[#0A0E1B]`}
                                    data-probability-outcome="home"
                                    data-leading={s.leadingOutcome === 'home'}
                                    style={{ width: `${s.hwPct}%` }}>
                                    {s.hwPct >= 15 ? `${s.hwPct}%` : ''}
                                  </div>
                                  <div className={`${s.leadingOutcome === 'draw' ? 'analytics-prob-winner' : 'analytics-prob-mid'} flex items-center justify-center border-r border-[#0A0E1B]`}
                                    data-probability-outcome="draw"
                                    data-leading={s.leadingOutcome === 'draw'}
                                    style={{ width: `${s.dwPct}%` }}>
                                    {s.dwPct >= 12 ? `${s.dwPct}%` : ''}
                                  </div>
                                  <div className={`${s.leadingOutcome === 'away' ? 'analytics-prob-winner' : 'analytics-prob-low'} flex items-center justify-center`}
                                    data-probability-outcome="away"
                                    data-leading={s.leadingOutcome === 'away'}
                                    style={{ width: `${s.awPct}%` }}>
                                    {s.awPct >= 15 ? `${s.awPct}%` : ''}
                                  </div>
                                </div>
                                <div className="flex justify-between text-[10px] text-[#9ca3af] px-0.5">
                                  {s.impl_h !== null
                                    ? <span>Implied Market: {s.impl_h}% / {s.impl_d}% / {s.impl_a}% (home/draw/away)</span>
                                    : <span>Implied market odds unavailable</span>}
                                  <a className="analytics-recommendation-link" href="#desktop-recommendation">Recommendation →</a>
                                </div>
                                {(s.homeEdge?.hasValue || s.drawEdge?.hasValue || s.awayEdge?.hasValue) && (
                                  <div className="flex gap-2 mt-1.5 px-0.5">
                                    {s.homeEdge?.hasValue && <span className="bg-green-500/20 border border-green-500/30 text-green-400 text-[9px] px-2 py-0.5 rounded font-bold">{s.hName} Win +{s.homeEdge.edgePct}% Edge</span>}
                                    {s.drawEdge?.hasValue && <span className="bg-green-500/20 border border-green-500/30 text-green-400 text-[9px] px-2 py-0.5 rounded font-bold">Draw +{s.drawEdge.edgePct}% Edge</span>}
                                    {s.awayEdge?.hasValue && <span className="bg-green-500/20 border border-green-500/30 text-green-400 text-[9px] px-2 py-0.5 rounded font-bold">{s.aName} Win +{s.awayEdge.edgePct}% Edge</span>}
                                  </div>
                                )}
                              </div>
                              <div id="desktop-recommendation" className="analytics-recommendation bg-[#0f1926]/70 border border-[#1e2d3d] rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-[10px] font-bold text-[#d1d5db] uppercase tracking-widest">Top Recommendation</span>
                                  <span className={`analytics-confidence-badge text-[9px] font-bold px-2 py-0.5 rounded border ${s.confBadgeCls}`}>
                                    {s.confLbl} Confidence ({s.conf}%)
                                  </span>
                                </div>
                                <p className="text-xs text-[#d1d5db] leading-relaxed">{buildWhyText(predictionData)}</p>
                              </div>
                            </div>

                            <div className="bg-[#132235] border border-[#20334a] rounded-xl p-5 flex flex-col items-center justify-center gap-4 text-center">
                              <div className="w-20 h-20 rounded-full bg-gradient-to-b from-[#1c324b] to-[#132235] border border-[#2a4360] flex items-center justify-center shadow-inner">
                                <img src={predictionData.away?.logo || '/static/fallback_logo.png'} alt={s.aName}
                                  className="w-14 h-14 object-contain"
                                  onError={e => { e.target.onerror = null; e.target.src = '/static/fallback_logo.png'; }} />
                              </div>
                              <div>
                                <div className="text-lg font-extrabold text-white">{s.aName}</div>
                                <div className="text-[10px] font-bold text-[#e5e7eb] uppercase tracking-widest mt-1">Away</div>
                              </div>
                            </div>
                          </div>
                        </BlurredSection>

                        {/* Algorithmic Simulation */}
                        <BlurredSection isBlurred={isCurrentMatchLocked} router={router} onUnlock={() => handleUnlockClick(selectedFixture?.fixture?.id, 1)}>
                          <div className="analytics-simulation-card bg-[#132235] border border-[#20334a] rounded-xl p-5">
                            <div className="text-[10px] font-bold text-[#e5e7eb] uppercase tracking-widest border-b border-[#20334a] pb-3 mb-4">
                              Algorithmic Simulation
                            </div>
                            <div className="analytics-stat-strip grid grid-cols-2 md:grid-cols-5 gap-3">
                              <div className="analytics-stat-cell bg-[#0f1926]/50 border border-[#1e2d3d] rounded-lg p-3">
                                <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider mb-2">Expected Goals (xG)</div>
                                <div className="flex items-center gap-2 text-xl font-black">
                                  <span className="text-green-400">{s.xg_h}</span>
                                  <span className="text-sm text-[#9ca3af] font-normal">|</span>
                                  <span className="text-[#3EA8F5]">{s.xg_a}</span>
                                </div>
                              </div>
                              <div className="analytics-stat-cell bg-[#0f1926]/50 border border-[#1e2d3d] rounded-lg p-3">
                                <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider mb-2">Over 1.5 Goals</div>
                                <div className="text-xl font-black text-white">{s.over15Pct}%</div>
                              </div>
                              <div className="analytics-stat-cell bg-[#0f1926]/50 border border-[#1e2d3d] rounded-lg p-3">
                                <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider mb-2">Over 2.5 Goals</div>
                                <div className="text-xl font-black text-white">{s.over25Pct}%</div>
                              </div>
                              <div className="analytics-stat-cell bg-[#0f1926]/50 border border-[#1e2d3d] rounded-lg p-3">
                                <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider mb-2">Both Teams to Score</div>
                                <div className="text-xl font-black text-white">{s.bttsPct}%</div>
                              </div>
                              <div className="analytics-stat-cell bg-[#0f1926]/50 border border-[#1e2d3d] rounded-lg p-3">
                                <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider mb-2">Clean Sheet Likelihood</div>
                                <div className="flex justify-around">
                                  <div className="text-center">
                                    <div className="text-[8px] text-[#9ca3af] font-bold mb-1">{s.hAbbr}</div>
                                    <div className="text-base font-black text-green-400">{s.cs_h}%</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-[8px] text-[#9ca3af] font-bold mb-1">{s.aAbbr}</div>
                                    <div className="text-base font-black text-[#3EA8F5]">{s.cs_a}%</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </BlurredSection>

                        {/* Deep Performance Comparison */}
                        <BlurredSection isBlurred={isCurrentMatchLocked} router={router} onUnlock={() => handleUnlockClick(selectedFixture?.fixture?.id, 1)}>
                          <div className="bg-[#132235] border border-[#20334a] rounded-xl p-5">
                            <div className="text-[10px] font-bold text-[#e5e7eb] uppercase tracking-widest border-b border-[#20334a] pb-3 mb-4">
                              Deep Performance Comparison
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                              <div className="analytics-performance-cell">
                                <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider mb-3">Power Ratings (ELO)</div>
                                <div className="flex items-baseline justify-between mb-2">
                                  <span className="text-2xl font-black text-white">{s.elo_h}</span>
                                  <span className="text-xs font-bold text-[#9ca3af] uppercase">vs</span>
                                  <span className="text-2xl font-black text-[#9ca3af]">{s.elo_a}</span>
                                </div>
                                <div className="h-1.5 w-full bg-[#0f1926] rounded-full overflow-hidden flex">
                                  <div className="bg-[#3EA8F5] rounded-full transition-all duration-700" style={{ width: `${s.eloBarH}%` }} />
                                </div>
                              </div>
                              <div className="analytics-performance-cell">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider">Attack Efficiency</div>
                                  <div className="text-[9px] text-[#9ca3af]">{s.attPctH}% vs {100 - s.attPctH}%</div>
                                </div>
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[9px] font-bold text-[#e5e7eb] w-6 shrink-0">{s.hAbbr.slice(0,3)}</span>
                                    <div className="flex-1 h-1.5 bg-[#0f1926] rounded-full overflow-hidden">
                                      <div className="h-full bg-[#3EA8F5] rounded-full" style={{ width: `${s.attBarH}%` }} />
                                    </div>
                                    <span className="text-[10px] text-[#d1d5db] font-mono w-7 text-right shrink-0">{s.att_h}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[9px] font-bold text-[#e5e7eb] w-6 shrink-0">{s.aAbbr.slice(0,3)}</span>
                                    <div className="flex-1 h-1.5 bg-[#0f1926] rounded-full overflow-hidden">
                                      <div className="h-full bg-[#3EA8F5] rounded-full" style={{ width: `${100 - s.attBarH}%` }} />
                                    </div>
                                    <span className="text-[10px] text-[#d1d5db] font-mono w-7 text-right shrink-0">{s.att_a}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="analytics-performance-cell">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider">Defence <span className="normal-case font-normal">(lower=better)</span></div>
                                  <div className="text-[9px] text-[#9ca3af]">{s.defPctH}% vs {100 - s.defPctH}%</div>
                                </div>
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[9px] font-bold text-[#e5e7eb] w-6 shrink-0">{s.hAbbr.slice(0,3)}</span>
                                    <div className="flex-1 h-1.5 bg-[#0f1926] rounded-full overflow-hidden">
                                      <div className="h-full bg-[#3EA8F5] rounded-full" style={{ width: `${s.defBarH}%` }} />
                                    </div>
                                    <span className="text-[10px] text-[#d1d5db] font-mono w-7 text-right shrink-0">{s.defCon_h}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[9px] font-bold text-[#e5e7eb] w-6 shrink-0">{s.aAbbr.slice(0,3)}</span>
                                    <div className="flex-1 h-1.5 bg-[#0f1926] rounded-full overflow-hidden">
                                      <div className="h-full bg-[#3EA8F5] rounded-full" style={{ width: `${100 - s.defBarH}%` }} />
                                    </div>
                                    <span className="text-[10px] text-[#d1d5db] font-mono w-7 text-right shrink-0">{s.defCon_a}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="analytics-performance-cell">
                                <div className="text-[9px] font-bold text-[#e5e7eb] uppercase tracking-wider mb-3">Physical Recovery Index</div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div className={`bg-[#0f1926]/60 rounded-lg p-2.5 text-center border ${s.form_h >= 65 ? 'border-green-500/30' : s.form_h >= 45 ? 'border-yellow-500/30' : 'border-red-500/30'}`}>
                                    <div className="text-[8px] font-bold text-[#e5e7eb] mb-1">{s.hAbbr}</div>
                                    <div className={`text-lg font-black ${s.recClr_h}`}>{s.form_h}%</div>
                                    <div className={`text-[8px] font-bold mt-1 ${s.recClr_h}`}>{s.recSt_h}</div>
                                  </div>
                                  <div className={`bg-[#0f1926]/60 rounded-lg p-2.5 text-center border ${s.form_a >= 65 ? 'border-green-500/30' : s.form_a >= 45 ? 'border-yellow-500/30' : 'border-red-500/30'}`}>
                                    <div className="text-[8px] font-bold text-[#e5e7eb] mb-1">{s.aAbbr}</div>
                                    <div className={`text-lg font-black ${s.recClr_a}`}>{s.form_a}%</div>
                                    <div className={`text-[8px] font-bold mt-1 ${s.recClr_a}`}>{s.recSt_a}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </BlurredSection>
                      </>
                    );
                  })()}

                  {!loading && !error && !predictionData && selectedFixture && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6 text-center">
                      <p className="text-yellow-200 text-sm">Select a fixture to view ML predictions</p>
                    </div>
                  )}
                </main>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#1e2d3d] bg-[#0f1926]/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <p className="text-[10px] text-[#e5e7eb] text-center">
              Predictions are ML estimates (XGBoost + ELO + Poisson) and not guarantees ·
              Simulation &amp; analytics only — no cash wagering
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
